import React, { useMemo, useState } from 'react';
import { ExecutionSchedule } from '../types';
import { format, parseISO, startOfDay, differenceInMinutes } from 'date-fns';

interface TimelineItem {
  execution: ExecutionSchedule;
  time: Date;
  minutesFromStart: number;
}

interface VerticalTimelineProps {
  executions: ExecutionSchedule[];
  selectedDate: Date;
  onExecutionClick?: (execution: ExecutionSchedule) => void;
}

const statusConfig: Record<string, { color: string; ringColor: string; icon: string; label: string }> = {
  pending: { 
    color: 'bg-gray-400', 
    ringColor: 'ring-gray-300',
    icon: '⏳', 
    label: 'Pending' 
  },
  scheduled: { 
    color: 'bg-yellow-500', 
    ringColor: 'ring-yellow-300',
    icon: '📅', 
    label: 'Scheduled' 
  },
  running: { 
    color: 'bg-blue-500', 
    ringColor: 'ring-blue-300',
    icon: '▶️', 
    label: 'Running' 
  },
  completed: { 
    color: 'bg-green-500', 
    ringColor: 'ring-green-300',
    icon: '✓', 
    label: 'Completed' 
  },
  partially_completed: { 
    color: 'bg-orange-500', 
    ringColor: 'ring-orange-300',
    icon: '⚠', 
    label: 'Partial' 
  },
  partial: { 
    color: 'bg-orange-500', 
    ringColor: 'ring-orange-300',
    icon: '⚠', 
    label: 'Partial' 
  },
  failed: { 
    color: 'bg-red-500', 
    ringColor: 'ring-red-300',
    icon: '✕', 
    label: 'Failed' 
  },
};

const DEFAULT_STATUS_CONFIG = {
  color: 'bg-gray-400',
  ringColor: 'ring-gray-300',
  icon: '•',
  label: 'Unknown',
};

export const VerticalTimeline: React.FC<VerticalTimelineProps> = ({
  executions,
  selectedDate,
  onExecutionClick,
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const toggleCardExpand = (executionId: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  };

  // Filter and sort executions for selected date
  const timelineItems = useMemo<TimelineItem[]>(() => {
    const dayStart = startOfDay(selectedDate);
    
    return executions
      .filter((exec) => {
        const execTime = exec.timestamp || exec.scheduled_time;
        if (!execTime) return false;
        const execDate = parseISO(execTime);
        return (
          execDate.getFullYear() === dayStart.getFullYear() &&
          execDate.getMonth() === dayStart.getMonth() &&
          execDate.getDate() === dayStart.getDate()
        );
      })
      .map((exec) => {
        const time = parseISO(exec.timestamp || exec.scheduled_time || '');
        const minutesFromStart = differenceInMinutes(time, dayStart);
        return { execution: exec, time, minutesFromStart };
      })
      .sort((a, b) => a.minutesFromStart - b.minutesFromStart);
  }, [executions, selectedDate]);

  // Generate hour markers (6 AM to 11 PM)
  const hourMarkers = useMemo(() => {
    return Array.from({ length: 18 }, (_, i) => ({
      hour: i + 6,
      label: `${String(i + 6).padStart(2, '0')}:00`,
      minutesFromStart: (i + 6) * 60,
    }));
  }, []);

  if (timelineItems.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <div className="text-center">
          <div className="text-5xl mb-3">📅</div>
          <p className="text-lg">No executions scheduled for this day</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Date Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900">
          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {timelineItems.length} execution{timelineItems.length !== 1 ? 's' : ''} scheduled
        </p>
      </div>

      {/* Timeline Container - Grid Layout with 3 columns */}
      <div className="relative space-y-6">
        {/* Timeline Items */}
        {timelineItems.map((item, index) => {
          const config = statusConfig[item.execution.status] || DEFAULT_STATUS_CONFIG;
          const prevItem = index > 0 ? timelineItems[index - 1] : null;
          const timeSincePrev = prevItem 
            ? item.minutesFromStart - prevItem.minutesFromStart 
            : 0;
          
          // Add spacer if gap is more than 30 minutes
          const showSpacer = timeSincePrev > 30;

          return (
            <React.Fragment key={item.execution.execution_id}>
              {showSpacer && (
                <div className="grid grid-cols-[5rem_3rem_1fr] gap-4 items-center h-8">
                  <div></div>
                  <div className="relative">
                    {/* Vertical line segment */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-300 -translate-x-1/2 z-0" />
                    <div className="relative text-center text-xs text-gray-400 z-10 bg-white px-1">
                      ⋮ {Math.floor(timeSincePrev)} min
                    </div>
                  </div>
                  <div></div>
                </div>
              )}

              {/* Timeline Item - 3 Column Grid */}
              <div className="grid grid-cols-[5rem_3rem_1fr] gap-4 items-center group">
                {/* Column 1: Time */}
                <div className="text-right">
                  <span className="text-base font-bold text-gray-800">
                    {format(item.time, 'HH:mm')}
                  </span>
                </div>

                {/* Column 2: Icon with Line */}
                <div className="relative flex justify-center items-center">
                  {/* Vertical line segments */}
                  {index > 0 && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-1/2 h-[calc(100%+1.5rem)] w-0.5 bg-gray-300 z-0" />
                  )}
                  {index < timelineItems.length - 1 && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-1/2 h-[calc(100%+1.5rem)] w-0.5 bg-gray-300 z-0" />
                  )}
                  
                  {/* Icon - with white background to separate from line */}
                  <div className={`relative w-9 h-9 rounded-full ${config.color} border-4 border-white shadow-xl flex items-center justify-center z-10 text-white font-bold text-lg bg-white`}>
                    <div className={`w-full h-full rounded-full ${config.color} flex items-center justify-center`}>
                      {config.icon}
                    </div>
                  </div>
                </div>

                {/* Column 3: Card - 2 Column Layout */}
                <div
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer group-hover:scale-[1.01]"
                >
                  {/* Card Grid: 2 columns */}
                  <div className="grid grid-cols-[1fr_auto] gap-4">
                    {/* Left Column: Main Info */}
                    <div onClick={() => onExecutionClick?.(item.execution)}>
                      <h4 className="font-bold text-gray-900 text-base mb-2 leading-tight">
                        {item.execution.workflow_name}
                      </h4>
                      
                      {/* Actions - Collapsible */}
                      {item.execution.actions && item.execution.actions.length > 0 && (
                        <div className="mb-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCardExpand(item.execution.execution_id);
                            }}
                            className="text-sm text-blue-600 flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-1.5 border border-blue-100 hover:bg-blue-100 transition-colors w-full text-left"
                          >
                            <span className="text-base">🎯</span>
                            <span className="font-semibold flex-1">
                              {expandedCards.has(item.execution.execution_id) ? (
                                `${item.execution.actions.length} actions`
                              ) : (
                                <>
                                  {item.execution.actions.slice(0, 2).join(' → ')}
                                  {item.execution.actions.length > 2 && (
                                    <span className="text-blue-400 ml-1">+{item.execution.actions.length - 2}</span>
                                  )}
                                </>
                              )}
                            </span>
                            <span className="text-xs font-bold">{expandedCards.has(item.execution.execution_id) ? '▲' : '▼'}</span>
                          </button>
                          
                          {/* Expanded Actions List */}
                          {expandedCards.has(item.execution.execution_id) && (
                            <div className="mt-2 ml-3 pl-3 border-l-2 border-blue-200 space-y-1">
                              {item.execution.actions.map((action, idx) => (
                                <div key={idx} className="text-xs text-gray-700 flex items-center gap-1.5">
                                  <span className="text-blue-500 font-bold">{idx + 1}.</span>
                                  <span>{action}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                          <span>👤</span>
                          <span className="font-mono font-medium">{item.execution.account_id.slice(-6)}</span>
                        </span>
                      </div>
                    </div>

                    {/* Right Column: Metadata - Only Duration */}
                    <div className="flex flex-col items-end justify-start">
                      {item.execution.estimated_duration_minutes && (
                        <div className="flex items-center gap-1.5 bg-purple-50 px-2.5 py-1 rounded-md whitespace-nowrap text-xs text-purple-700 border border-purple-100">
                          <span>⏱️</span>
                          <span className="font-semibold">{item.execution.estimated_duration_minutes} min</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Error Message (if failed) */}
                  {item.execution.status === 'failed' && item.execution.error_message && (
                    <div className="mt-3 pt-3 border-t border-red-100 bg-red-50 -mx-4 -mb-4 px-4 pb-4 rounded-b-xl">
                      <div className="flex items-start gap-2 text-xs text-red-700">
                        <span>⚠️</span>
                        <span className="flex-1">{item.execution.error_message}</span>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              </React.Fragment>
            );
          })}

        {/* End Marker */}
        <div className="grid grid-cols-[5rem_3rem_1fr] gap-4 items-center mt-6">
          <div></div>
          <div className="relative flex justify-center">
            <div className="w-8 h-8 rounded-full bg-gray-200 border-4 border-white flex items-center justify-center z-10">
              <span className="text-xs">🏁</span>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            End of scheduled executions
          </div>
        </div>
      </div>
    </div>
  );
};
